import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Item from "../Components/Item/Item";
import { backend_url } from "../App";
import Spinner from "../Components/Spinner/Spinner";
import { useSearchParams } from "react-router-dom";

const ShopCategory = (props) => {
  const navigate = useNavigate();
  const { subcategory } = useParams();
  const [allproducts, setAllProducts] = useState([]);
  const [sortOption, setSortOption] = useState("Bestselling");
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromQuery = parseInt(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(pageFromQuery);
  const productsPerPage = 16;

  useEffect(() => {
    const fetchInfo = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${backend_url}/allproducts`);
        const data = await res.json();
        setAllProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
      setLoading(false);
    };

    fetchInfo();
  }, []);

  useEffect(() => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}, [currentPage]);

  useEffect(() => {
    const page = parseInt(searchParams.get("page")) || 1;
    setCurrentPage(page);
  }, [searchParams]);

  // new_price holds the discount %, so the real selling price must be derived from old_price
  const effectivePrice = (item) =>
    (item.old_price * (100 - item.new_price)) / 100;

  const isAllProducts = subcategory?.toLowerCase().trim() === "all";
  const minPrice = searchParams.get("minPrice");
  const maxPrice = searchParams.get("maxPrice");

  const filteredProducts = allproducts
    .filter(
      (item) =>
        isAllProducts ||
        item.subcategory?.toLowerCase().trim() ===
          subcategory?.toLowerCase().trim()
    )
    .filter((item) => !minPrice || effectivePrice(item) >= Number(minPrice))
    .filter((item) => !maxPrice || effectivePrice(item) <= Number(maxPrice));

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortOption) {
      case "Bestselling": {
        const aBest = a.bestseller?.toLowerCase() === "yes" ? 0 : 1;
        const bBest = b.bestseller?.toLowerCase() === "yes" ? 0 : 1;
        return aBest - bBest;
      }
      case "A-Z":
        return a.name.localeCompare(b.name);
      case "Z-A":
        return b.name.localeCompare(a.name);
      case "Price high to low":
        return effectivePrice(b) - effectivePrice(a);
      case "Price low to high":
        return effectivePrice(a) - effectivePrice(b);
      case "Date new to old":
        return new Date(b.date) - new Date(a.date);
      case "Date old to new":
        return new Date(a.date) - new Date(b.date);
      default:
        return 0;
    }
  });

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);

  const goToPage = (page) => {
    setCurrentPage(page);
    const next = new URLSearchParams(searchParams);
    next.set("page", page);
    setSearchParams(next);
  };

  // Windowed page list: first, last, and a couple pages either side of the
  // current one, with "..." filling the gaps — avoids a wall of buttons
  // when there are dozens of pages.
  const getPageItems = (current, total, delta = 1) => {
    const pages = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        pages.push(i);
      }
    }
    const items = [];
    let prev;
    for (const page of pages) {
      if (prev !== undefined) {
        items.push(page - prev === 2 ? prev + 1 : page - prev > 1 ? "…" : null);
      }
      items.push(page);
      prev = page;
    }
    return items.filter((item) => item !== null);
  };
  const pageItems = getPageItems(currentPage, totalPages);

  // Format the subcategory (replace hyphens and capitalize first letter)
  const formattedHeader = isAllProducts
    ? "All Products"
    : subcategory
    ? subcategory
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "Category";

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <div className="w-full max-w-screen-xl mx-auto px-4">
          {/* Back button + breadcrumb */}
          <div className="flex items-center gap-3 py-3">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-[var(--brand-primary-200)] bg-white
                         hover:border-[var(--brand-primary-500)] hover:bg-[var(--brand-primary-600)] hover:shadow-lg transition-all duration-300"
            >
              <svg
                className="w-4 h-4 text-[var(--brand-primary-600)] group-hover:text-white transition-colors duration-300 group-hover:-translate-x-0.5 transform"
                fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              <span className="text-xs font-bold text-[var(--brand-primary-600)] group-hover:text-white transition-colors duration-300 uppercase tracking-wide">
                Back
              </span>
            </button>
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <span className="hover:text-[var(--brand-primary-500)] cursor-pointer transition-colors" onClick={() => navigate('/')}>Home</span>
              <svg className="w-3 h-3 text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-[var(--brand-primary-700)] font-semibold truncate max-w-[200px] sm:max-w-xs">{formattedHeader}</span>
            </div>
          </div>
          {/* Header and Sort Section */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6">
            {/* Header Section */}
            <div>
              <h2
                className="font-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-4 text-gray-900 leading-none tracking-tight"
                style={{ letterSpacing: "-0.02em" }}
              >
                {formattedHeader}
              </h2>
              {(minPrice || maxPrice) && (
                <div className="mt-2 inline-flex items-center gap-2 bg-[var(--brand-primary-50)] border border-[var(--brand-primary-200)] text-[var(--brand-primary-700)] text-xs font-semibold px-3 py-1.5 rounded-full">
                  <span>
                    {minPrice && maxPrice
                      ? `₹${minPrice} – ₹${maxPrice}`
                      : minPrice
                      ? `Above ₹${minPrice}`
                      : `Under ₹${maxPrice}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSearchParams({})}
                    className="hover:text-[var(--brand-primary-900)]"
                    aria-label="Clear price filter"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            {/* Sort by Dropdown — pinned to the right end */}
            <div className="flex items-center gap-2 self-start md:self-auto md:ml-auto md:mb-1">
              <label
                htmlFor="sort"
                className="text-sm text-gray-500 whitespace-nowrap"
              >
                Sort by:
              </label>
              <select
                id="sort"
                className="border border-[var(--brand-primary-200)] bg-white px-3 py-2 rounded-lg text-sm cursor-pointer
                           focus:ring-2 focus:ring-[var(--brand-primary-400)] focus:border-[var(--brand-primary-400)] outline-none
                           hover:border-[var(--brand-primary-300)] transition-colors"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option>Bestselling</option>
                <option>A-Z</option>
                <option>Z-A</option>
                <option>Price high to low</option>
                <option>Price low to high</option>
                <option>Date new to old</option>
                <option>Date old to new</option>
              </select>
            </div>
          </div>

          {/* Product Grid - 2 columns on mobile */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            {currentProducts.map((item) => {
              const offerPrice =
                (item.old_price * (100 - item.new_price)) / 100;
              const discountPercentage =
                ((item.old_price - offerPrice) / item.old_price) * 100;

              return (
                <div key={item.id} className="aspect-square">
                  {" "}
                  {/* Square container */}
                  <Item
                    id={item.id}
                    name={item.name}
                    image={item.images?.[0] || ""}
                    new_price={offerPrice.toFixed(2)}
                    old_price={item.old_price}
                    discountPercentage={discountPercentage.toFixed(0)}
                    quantity={item.quantity}
                    scale={item.scale}
                  />
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mb-6">
              <button
                disabled={currentPage === 1}
                onClick={() => goToPage(currentPage - 1)}
                aria-label="Previous page"
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-md text-sm
                           bg-white border border-[var(--brand-primary-200)] text-[var(--brand-primary-700)]
                           hover:bg-[var(--brand-primary-50)] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
              >
                ‹
              </button>

              {pageItems.map((item, idx) =>
                item === "…" ? (
                  <span key={`dots-${idx}`} className="w-8 text-center text-sm text-gray-400 select-none">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => goToPage(item)}
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-md text-xs sm:text-sm transition-colors ${
                      currentPage === item
                        ? "bg-[var(--brand-primary-600)] text-white font-semibold"
                        : "bg-gray-100 text-gray-700 hover:bg-[var(--brand-primary-500)] hover:text-white"
                    }`}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                disabled={currentPage === totalPages}
                onClick={() => goToPage(currentPage + 1)}
                aria-label="Next page"
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-md text-sm
                           bg-white border border-[var(--brand-primary-200)] text-[var(--brand-primary-700)]
                           hover:bg-[var(--brand-primary-50)] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ShopCategory;
